import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiSave, FiSend } from "react-icons/fi";
import { Modal, Button, FormField, Banner, DraftNotice } from "../common";
import useFormDraft from "../../hooks/useFormDraft";
import useAsync from "../../hooks/useAsync";
import { getActiveKras, getKpisForKra, createProposal, updateProposal } from "../../services/kraKpiProposalApi";
import { KPI_DIRECTIONS, KPI_FREQUENCIES, parseScaleUnit, scaleTextFromUnit, unitFromScaleText } from "../../utils/constants";
import { getErrorMessage } from "../../utils/errors";
import Select from "../common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

// [UPDATED] KRAs and KPIs are measured on 1 to 5 by default, like goals. The field is no longer shown.
// A saved scale such as "Scale (1-10)" is kept; any other unit falls back to 1 to 5.
const DEFAULT_SCALE = "1 to 5";
const scaleFields = (unit) => ({ scale: parseScaleUnit(unit) ? scaleTextFromUnit(unit) : DEFAULT_SCALE });
const EMPTY = {
  type: "KRA", parent: "", kpiId: "", title: "", description: "", unit: unitFromScaleText(DEFAULT_SCALE), scale: DEFAULT_SCALE, direction: "higher", frequency: "Once",
  forAppraisal: true, weightage: "", target: "", dueDate: "",
};

function fromProposal(p) {
  return {
    type: p.type,
    parent: p.type === "KPI" ? (p.parentProposalId ? `prop:${p.parentProposalId}` : p.kraId ? `lib:${p.kraId}` : "") : (p.kraId ? `lib:${p.kraId}` : ""),
    kpiId: p.kpiId || "",
    title: p.title, description: p.description, unit: p.unit, ...scaleFields(p.unit), direction: p.direction, frequency: p.frequency,
    forAppraisal: Boolean(p.recordId), weightage: p.weightage, target: p.target, dueDate: p.dueDate,
  };
}

export default function ProposalFormModal({ open, mode = "create", proposal, recordId, sheetOpen = true, cycleName, dueDefault, openKraProposals = [], onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const kras = useAsync(() => getActiveKras(), [], { skip: !open });
  const libraryKra = form.parent.startsWith("lib:") ? form.parent.slice(4) : "";
  const kpis = useAsync(() => getKpisForKra(libraryKra), [libraryKra], { skip: !open || form.type !== "KPI" || !libraryKra });

  useEffect(() => {
    if (!open) return;
    setForm(proposal ? fromProposal(proposal) : { ...EMPTY, forAppraisal: Boolean(recordId) && sheetOpen, dueDate: dueDefault || "" });
    setError("");
  }, [open, proposal, recordId, sheetOpen, dueDefault]);

  // [NEW] Keep unsaved proposal text in this browser until it is saved or sent.
  const draft = useFormDraft(`proposal:${mode}:${proposal?.id || `new:${recordId || ""}`}`, form, setForm, { active: open });

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(""); };
  const appraisal = mode === "review" ? Boolean(proposal?.recordId) : form.forAppraisal && Boolean(recordId || proposal?.recordId);
  const targetRecord = appraisal ? (proposal?.recordId || recordId) : null;
  const parents = useMemo(() => {
    const list = openKraProposals.filter((p) => (!proposal || p.id !== proposal.id) && (p.recordId || null) === (targetRecord || null));
    const saved = proposal?.parentProposalId;
    if (saved && form.parent === `prop:${saved}` && !list.some((p) => String(p.id) === saved)) {
      list.push({ id: saved, title: proposal.parentProposalTitle || `Proposal #${saved}`, statusLabel: "Proposed" });
    }
    return list;
  }, [openKraProposals, proposal, targetRecord, form.parent]);

  const pickLibraryKra = (value) => {
    const kra = (kras.data || []).find((k) => `lib:${k.id}` === value);
    set(form.type === "KRA" && kra ? { parent: value, title: form.title || kra.name, description: form.description || kra.description } : { parent: value, kpiId: "" });
  };
  const pickLibraryKpi = (id) => {
    const kpi = (kpis.data || []).find((k) => k.id === id);
    set(kpi ? { kpiId: id, title: kpi.name, description: kpi.description, unit: kpi.unit, ...scaleFields(kpi.unit), direction: kpi.direction, frequency: kpi.frequency } : { kpiId: "" });
  };

  const payload = () => ({
    type: form.type,
    recordId: appraisal ? (proposal?.recordId || recordId) : null,
    kraId: libraryKra,
    kpiId: form.type === "KPI" ? form.kpiId : "",
    parentProposalId: form.type === "KPI" && form.parent.startsWith("prop:") ? form.parent.slice(5) : "",
    title: form.title, description: form.description, unit: unitFromScaleText(form.scale || DEFAULT_SCALE), direction: form.direction, frequency: form.frequency, // [UPDATED] 1 to 5 unless the item already has its own scale
    weightage: appraisal ? form.weightage : "", target: appraisal ? form.target : "", dueDate: appraisal ? form.dueDate : "",
  });

  const check = (sending) => {
    if (!String(form.title).trim()) return "Give it a short title.";
    if (sending || mode === "review") {
      if (appraisal && (!Number(form.weightage) || Number(form.weightage) > 100)) return "Enter a weight between 1 and 100.";
      if (appraisal && !(Number(form.target) > 0)) return "Enter a target greater than 0.";
      if (appraisal && !form.dueDate) return "Pick a due date inside the cycle.";
    }
    return null;
  };

  const save = async (sending) => {
    const problem = check(sending);
    if (problem) { setError(problem); return; }
    setBusy(sending ? "send" : "save");
    try {
      const { data } = mode === "create" ? await createProposal(payload(), { submit: sending }) : await updateProposal(proposal, payload());
      toast.success(mode === "review" ? "Proposal updated. The employee is notified." : sending ? "Sent to your manager for approval." : mode === "edit" ? "Changes saved. Use Send when it is ready." : "Saved as draft.");
      draft.clear();
      onSaved?.(data, { sending });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const title = mode === "create" ? "Propose a KRA or KPI" : mode === "review" ? `Edit ${proposal?.employeeName}'s proposal` : "Edit proposal";
  return (
    <Modal
      open={open}
      title={title}
      subtitle={mode === "review" ? "Your changes are saved on the proposal; approve it afterwards." : "Your reporting manager approves, returns or rejects it."}
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={Boolean(busy)}>Cancel</Button>
          {mode === "create" && <Button variant="secondary" icon={FiSave} onClick={() => save(false)} loading={busy === "save"} disabled={Boolean(busy)}>Save draft</Button>}
          {mode === "create"
            ? <Button icon={FiSend} onClick={() => save(true)} loading={busy === "send"} disabled={Boolean(busy)}>Send for approval</Button>
            : <Button icon={FiSave} onClick={() => save(false)} loading={busy === "save"} disabled={Boolean(busy)}>Save changes</Button>}
        </>
      }
    >
      <DraftNotice draft={draft} />
      <div className="form-grid">
        {mode === "create" && (
          <FormField label="What are you proposing?" className="span-2">
            <Select value={form.type} onChange={(e) => set({ type: e.target.value, parent: "", kpiId: "" })}>
              <option value="KRA">KRA — a key result area</option>
              <option value="KPI">KPI — a measurable indicator</option>
            </Select>
          </FormField>
        )}
        {form.type === "KRA" ? (
          <FormField label="Start from a library KRA (optional)" className="span-2" hint="Leave empty to define a new KRA of your own.">
            <Select value={form.parent} onChange={(e) => pickLibraryKra(e.target.value)}>
              <option value="">New KRA</option>
              {(kras.data || []).map((k) => <option key={k.id} value={`lib:${k.id}`}>{k.name}</option>)}
            </Select>
          </FormField>
        ) : (
          <>
            <FormField label="Parent KRA" hint="The KRA this KPI measures. Choosing a library KRA lists only its KPIs below.">
              <Select value={form.parent} onChange={(e) => pickLibraryKra(e.target.value)}>
                <option value="">No parent KRA</option>
                {parents.length > 0 && (
                  <optgroup label={mode === "review" ? "Proposed KRAs" : "My proposed KRAs"}>
                    {parents.map((p) => <option key={p.id} value={`prop:${p.id}`}>{p.title} ({p.statusLabel.toLowerCase()})</option>)}
                  </optgroup>
                )}
                <optgroup label="KRA library">
                  {(kras.data || []).map((k) => <option key={k.id} value={`lib:${k.id}`}>{k.name}{k.kpiCount ? ` (${k.kpiCount} KPIs)` : ""}</option>)}
                </optgroup>
              </Select>
            </FormField>
            <FormField label="Library KPI (optional)" hint={libraryKra ? (kpis.loading ? "Loading the KPIs of this KRA..." : `${(kpis.data || []).length} KPI(s) mapped to this KRA`) : "Pick a library KRA first"}>
              <Select value={form.kpiId} disabled={!libraryKra || !(kpis.data || []).length} onChange={(e) => pickLibraryKpi(e.target.value)}>
                <option value="">New KPI</option>
                {(kpis.data || []).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Select>
            </FormField>
          </>
        )}
        <FormField label="Title" required className="span-2">
          <input value={form.title} maxLength={300} onChange={(e) => set({ title: e.target.value })} />
        </FormField>
        <FormField label="Description" className="span-2">
          <textarea rows={2} value={form.description} maxLength={5000} onChange={(e) => set({ description: e.target.value })} />
        </FormField>
        {/* [UPDATED] No measurement scale field: KRAs and KPIs use 1 to 5 automatically. */}
        <FormField label="Direction">
          <Select value={form.direction} onChange={(e) => set({ direction: e.target.value })}>
            {KPI_DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>
        </FormField>
        {form.type === "KPI" && (
          <FormField label="Measured">
            <Select value={form.frequency} onChange={(e) => set({ frequency: e.target.value })}>
              {KPI_FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </FormField>
        )}
        {mode !== "review" && (recordId || proposal?.recordId) && (
          <label className="checkbox span-2">
            <input type="checkbox" checked={form.forAppraisal} disabled={!sheetOpen && !form.forAppraisal} onChange={(e) => set({ forAppraisal: e.target.checked, parent: form.parent.startsWith("prop:") ? "" : form.parent })} />
            <span>
              Add it to my KRA &amp; KPI sheet{cycleName ? ` for ${cycleName}` : ""} when approved
              {!sheetOpen && (
                <small className="muted" style={{ display: "block" }}>
                  Not available now: your goal sheet is already submitted or approved. This proposal goes to the library only.
                  Ask your manager to return your goal sheet if it must be added to this cycle.
                </small>
              )}
            </span>
          </label>
        )}
        {appraisal && (
          <>
            <FormField label="Weight %" required>
              <input type="number" min="1" max="100" value={form.weightage} onChange={(e) => set({ weightage: e.target.value })} />
            </FormField>
            <FormField label="Target" required>
              <input type="number" step="any" min="0" value={form.target} onChange={(e) => set({ target: e.target.value })} />
            </FormField>
            <FormField label="Due date" required>
              <input type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
            </FormField>
          </>
        )}
      </div>
      {appraisal && mode !== "review" && (
        <Banner tone="info">Approved items are added to your sheet only while goal setting is open. KRA weights must total 100%. For a KPI proposal the weight is its share inside its KRA (the KPIs of a KRA total 100%); on Assign KRA & KPI, Share KPI weights equally fixes the shares after approval.</Banner>
      )}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
}